import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, CheckCircle, Edit2, Trash2, Plus, Save, X } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const ValuePropositionsCRUD = ({ config, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [sectionContent, setSectionContent] = useState({
    title: 'Why Top Lenders Choose Our Platform',
    subtitle: 'Discover the proven value our AI agents deliver to mortgage professionals worldwide',
    trust_text: 'Trusted by 500+ lenders'
  });
  
  const valuePropositions = config?.value_propositions || {};
  const features = valuePropositions.features || [];
  
  const [formData, setFormData] = useState({
    icon: '⚡',
    title: '',
    subtitle: '',
    description: '',
    benefits: ['', '', ''],
    highlight: '',
    color: 'from-blue-500 to-blue-600'
  });

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    // Always update sectionContent when valuePropositions changes
    setSectionContent({
      title: valuePropositions.title || 'Why Top Lenders Choose Our Platform',
      subtitle: valuePropositions.subtitle || 'Discover the proven value our AI agents deliver to mortgage professionals worldwide',
      trust_text: valuePropositions.trust_text || 'Trusted by 500+ lenders'
    });
  }, [valuePropositions]);

  const resetForm = () => {
    setFormData({
      icon: '⚡',
      title: '',
      subtitle: '',
      description: '',
      benefits: ['', '', ''],
      highlight: '',
      color: 'from-blue-500 to-blue-600'
    });
    setEditingIndex(-1);
    setShowAddForm(false);
  };

  const handleEdit = (index) => {
    const feature = features[index];
    setFormData({
      icon: feature.icon || '⚡',
      title: feature.title || '',
      subtitle: feature.subtitle || '',
      description: feature.description || '',
      benefits: feature.benefits || ['', '', ''],
      highlight: feature.highlight || '',
      color: feature.color || 'from-blue-500 to-blue-600'
    });
    setEditingIndex(index);
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let updatedFeatures = [...features];
      
      if (editingIndex >= 0) {
        // Update existing
        updatedFeatures[editingIndex] = { ...formData };
      } else {
        // Add new
        updatedFeatures.push({ ...formData });
      }

      await axios.put(`${API_BASE_URL}/api/admin/config/value_propositions/features`, {
        value: updatedFeatures,
        data_type: 'json'
      }, {
        headers: getAuthHeaders()
      });

      await onUpdate();
      resetForm();
      alert('Value proposition saved successfully!');
    } catch (error) {
      console.error('Error saving value proposition:', error);
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error saving value proposition');
      }
    }
    setLoading(false);
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this value proposition?')) return;
    
    setLoading(true);
    try {
      const updatedFeatures = features.filter((_, i) => i !== index);
      
      await axios.put(`${API_BASE_URL}/api/admin/config/value_propositions/features`, {
        value: updatedFeatures,
        data_type: 'json'
      }, {
        headers: getAuthHeaders()
      });

      await onUpdate();
      alert('Value proposition deleted successfully!');
    } catch (error) {
      console.error('Error deleting value proposition:', error);
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error deleting value proposition');
      }
    }
    setLoading(false);
  };

  const updateSectionContent = (field, value) => {
    setSectionContent(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
    setSaveStatus('');
  };

  const saveSectionContent = async () => {
    setLoading(true);
    setSaveStatus('');
    
    try {
      // Save each section content field
      for (const [field, value] of Object.entries(sectionContent)) {
        await axios.put(`${API_BASE_URL}/api/admin/config/value_propositions/${field}`, {
          value: value,
          data_type: 'string'
        }, {
          headers: getAuthHeaders()
        });
      }

      // Reload config data first
      await onUpdate();
      
      // Then update UI state after fresh data is loaded
      setHasUnsavedChanges(false);
      setSaveStatus('success');
      
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving section content:', error);
      setSaveStatus('error');
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error saving section content. Please try again.');
      }
    }
    
    setLoading(false);
  };

  const discardSectionChanges = () => {
    if (hasUnsavedChanges && window.confirm('Are you sure you want to discard your changes?')) {
      setSectionContent({
        title: config?.value_propositions?.title || 'Why Top Lenders Choose Our Platform',
        subtitle: config?.value_propositions?.subtitle || 'Discover the proven value our AI agents deliver to mortgage professionals worldwide',
        trust_text: config?.value_propositions?.trust_text || 'Trusted by 500+ lenders'
      });
      setHasUnsavedChanges(false);
      setSaveStatus('');
    }
  };

  const colorOptions = [
    { value: 'from-blue-500 to-blue-600', label: 'Blue' },
    { value: 'from-red-500 to-red-600', label: 'Red' },
    { value: 'from-green-500 to-green-600', label: 'Green' },
    { value: 'from-purple-500 to-purple-600', label: 'Purple' },
    { value: 'from-indigo-500 to-indigo-600', label: 'Indigo' },
    { value: 'from-orange-500 to-orange-600', label: 'Orange' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <Star className="h-5 w-5 text-blue-600 mr-2" />
          <h4 className="text-lg font-semibold text-blue-900">Value Propositions Section</h4>
        </div>
        <p className="text-blue-700 mt-2">
          Manage the value propositions section including title, subtitle, and individual proposition cards.
        </p>
      </div>

      {/* Section Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h5 className="font-semibold text-gray-900 mb-4">Section Content</h5>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Title
            </label>
            <input
              type="text"
              value={sectionContent.title}
              onChange={(e) => {
                setSectionContent({ ...sectionContent, title: e.target.value });
                updateSectionContent('title', e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Subtitle
            </label>
            <textarea
              value={sectionContent.subtitle}
              onChange={(e) => {
                setSectionContent({ ...sectionContent, subtitle: e.target.value });
                updateSectionContent('subtitle', e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trust Text (e.g., "Trusted by 500+ lenders")
            </label>
            <input
              type="text"
              value={sectionContent.trust_text}
              onChange={(e) => {
                setSectionContent({ ...sectionContent, trust_text: e.target.value });
                updateSectionContent('trust_text', e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* Save Status */}
      {saveStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-green-800 font-medium">Value propositions section saved successfully!</span>
          </div>
        </div>
      )}

      {saveStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <X className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">Error saving value propositions section. Please try again.</span>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Edit2 className="h-5 w-5 text-yellow-600 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">You have unsaved changes</h4>
                <p className="text-sm text-yellow-700">Save your changes to update the value propositions section.</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={discardSectionChanges}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={loading}
              >
                Discard
              </button>
              <button
                onClick={saveSectionContent}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center space-x-2"
                disabled={loading}
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Value Propositions Management */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h5 className="font-semibold text-gray-900">Value Propositions</h5>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
            disabled={loading}
          >
            <Plus className="h-4 w-4" />
            <span>Add Value Proposition</span>
          </button>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h6 className="font-medium text-gray-900">
                {editingIndex >= 0 ? 'Edit Value Proposition' : 'Add New Value Proposition'}
              </h6>
              <button
                type="button"
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon (emoji)
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="⚡"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color Gradient
                </label>
                <select
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {colorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Benefits (3 items)
              </label>
              {formData.benefits.map((benefit, index) => (
                <input
                  key={`benefit-input-${index}`}
                  type="text"
                  value={benefit}
                  onChange={(e) => {
                    const newBenefits = [...formData.benefits];
                    newBenefits[index] = e.target.value;
                    setFormData({ ...formData, benefits: newBenefits });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-2"
                  placeholder={`Benefit ${index + 1}`}
                  required
                />
              ))}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Highlight Text
              </label>
              <input
                type="text"
                value={formData.highlight}
                onChange={(e) => setFormData({ ...formData, highlight: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Key highlight or call-to-action"
                required
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                disabled={loading}
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Saving...' : 'Save Value Proposition'}</span>
              </button>
            </div>
          </form>
        )}

        {/* Value Propositions List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, index) => (
            <div key={`value-prop-${index}`} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Header with gradient */}
              <div className={`bg-gradient-to-r ${feature.color} p-4 text-white`}>
                <div className="text-2xl mb-2">{feature.icon}</div>
                <h6 className="font-semibold text-sm">{feature.title}</h6>
                <p className="text-xs text-white text-opacity-80">{feature.subtitle}</p>
              </div>

              {/* Content */}
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
                
                <div className="space-y-1 mb-3">
                  {feature.benefits?.map((benefit, idx) => (
                    <div key={`benefit-display-${idx}`} className="flex items-center text-xs">
                      <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-600">{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-50 rounded p-2 text-xs text-gray-700 mb-3">
                  💡 {feature.highlight}
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={() => handleEdit(index)}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
                    disabled={loading}
                  >
                    <Edit2 className="h-3 w-3" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-red-600 hover:text-red-800 text-sm flex items-center space-x-1"
                    disabled={loading}
                  >
                    <Trash2 className="h-3 w-3" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {features.length === 0 && (
          <p className="text-gray-500 text-center py-8">
            No value propositions added yet. Click "Add Value Proposition" to get started.
          </p>
        )}
      </div>
    </div>
  );
};

export default ValuePropositionsCRUD;
